import { useState } from 'react';
import { Story } from '../../schema';

interface UseEditStoryModalReturn {
  isOpen: boolean;
  editingStory: Story | null;
  openForEdit: (story: Story) => void;
  openForCreate: () => void;
  switchToEditMode: (story: Story) => void;
  close: () => void;
}

/**
 * Hook to manage story editing modal state.
 * Platform-agnostic: Pure state management, works in React Native.
 *
 * The mode is derived from whether editingStory is null:
 * - story === null: Create mode (only shows basic details, no photos)
 * - story !== null: Edit mode (shows all fields including photos)
 *
 * @returns Modal state and control functions
 */
export const useEditStoryModal = (): UseEditStoryModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingStory, setEditingStory] = useState<Story | null>(null);

  const openForEdit = (story: Story) => {
    setEditingStory(story);
    setIsOpen(true);
  };

  const openForCreate = () => {
    // Open with null story (create mode is derived from null story)
    setEditingStory(null);
    setIsOpen(true);
  };

  const switchToEditMode = (story: Story) => {
    // After creating story, set the story (edit mode is derived from non-null story)
    setEditingStory(story);
  };

  const close = () => {
    setIsOpen(false);
    setEditingStory(null);
  };

  return {
    isOpen,
    editingStory,
    openForEdit,
    openForCreate,
    switchToEditMode,
    close,
  };
};
